import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import {
  listCacheCategories,
  resetCache as resetCacheService,
} from "./cache-service";

const CacheView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await listCacheCategories(apiCall);

      if (response && response.items) {
        setCategories(response.items);
      } else {
        setCategories([]);
      }
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch cache categories");
      setLoading(false);
    }
  };

  const handleResetClick = category => {
    setSelectedCategory(category);
    setResetDialogOpen(true);
  };

  const handleResetConfirm = async () => {
    if (!selectedCategory) {
      return;
    }

    try {
      setResetting(true);
      const response = await resetCacheService(
        apiCall,
        selectedCategory.cluster,
        selectedCategory.prefix,
      );

      if (response && response.success) {
        successToast(
          `Cache reset successfully for ${selectedCategory.name}`,
        );
        setResetDialogOpen(false);
        setSelectedCategory(null);
      } else {
        throw new Error("Reset failed");
      }
      setResetting(false);
    } catch (error) {
      errorToast("Failed to reset cache");
      setResetting(false);
    }
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
    setSelectedCategory(null);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      <Grid container direction="column" spacing={3}>
        {/* Header */}
        <Grid item>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h1">
              Cache Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCategories}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Grid>

        {/* Categories List */}
        <Grid item>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {categories.length === 0 ? (
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                  >
                    No cache categories available
                  </Typography>
                </Grid>
              ) : (
                categories.map((category, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #e0e0e0",
                        "&:hover": {
                          boxShadow: 2,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {category.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Cluster: {category.cluster}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Prefix: {category.prefix}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          disabled={resetting}
                          onClick={() => handleResetClick(category)}
                          fullWidth
                        >
                          Reset
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleResetCancel}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Confirm Cache Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            Are you sure you want to reset the cache for{" "}
            <strong>{selectedCategory?.name}</strong>? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetCancel} disabled={resetting}>
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            color="error"
            variant="contained"
            disabled={resetting}
          >
            {resetting ? "Resetting..." : "Reset"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CacheView;

